import React, { useState, useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import {
    IonRadioGroup,
    IonRadio,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonContent,
} from "@ionic/react";


// Union type for all question types
type Question =
    | MultipleChoiceQuestion
    | FillInTheBlankQuestion
    | TrueFalseQuestion

// Question Type Definitions
interface BaseQuestion {
    question: string;
}

interface MultipleChoiceQuestion extends BaseQuestion {
    type: "multiple-choice";
    options: string[];
    answer: string;
}

interface FillInTheBlankQuestion extends BaseQuestion {
    type: "fill-in-the-blank";
    options: string[];
    answer: string;
}

interface TrueFalseQuestion extends BaseQuestion {
    type: "true-false";
    answer: string;
}




const SurroundingPlant: React.FC = () => {
    const [questions, setQuestions] = useState<Question[]>([]);
    const [currentQuestion, setCurrentQuestion] = useState<Question | null>(null);
    const [selectedOption, setSelectedOption] = useState<string | null>(null);
    const [matchOption, setMatchOption] = useState<string[] | null>(null);
    const [showImage, setShowImage] = useState(false);
    const [currentIndex, setCurrentIndex] = useState(0);
    const modal = useRef<HTMLIonModalElement>(null);

    const loadRandomSyllabusQuestions = async () => {

        try {
            let data: Question[] = [];
            if (process.env.REACT_APP_DEVELOPMENT !== "WEB") {
                const response = await fetch(
                    process.env.REACT_APP_API_URL + "ClassThreeScience/SurroundingPlant.json"
                );
                data = await response.json();
            } else {
                const module = await import(
                    `../../../datas/ClassThreeScience/SurroundingPlant.json`
                );
                data = module.default as Question[];
            }

            setQuestions(data);
            const shuffledQuestions = shuffleArray(data);
            setQuestions(shuffledQuestions);
            setCurrentQuestion(shuffledQuestions[currentIndex]);
        } catch (err) {
            console.error("Failed to load questions:", err);
        }
    };

    const handleAnswerSubmit = (option: string) => {
        setSelectedOption(option);
        let isCorrect;
        if (!currentQuestion) return;

        let correctAnswer: string | boolean = "";
        if (
            currentQuestion.type === "multiple-choice" ||
            currentQuestion.type === "fill-in-the-blank"
        ) {
            correctAnswer = currentQuestion.answer;

            isCorrect = option.trim().toLowerCase() === correctAnswer.trim().toLowerCase()

        } else if (currentQuestion.type === "true-false") {
            correctAnswer = currentQuestion.answer;
            isCorrect = option.trim().toLowerCase() === String(correctAnswer).toLowerCase()
        }

        if (isCorrect == true) {
            setShowImage(true);
            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < questions.length) {
                    setCurrentIndex(nextIndex);
                    setShowImage(false);
                    setSelectedOption(null);
                    setCurrentQuestion(questions[nextIndex]);
                }
                else {
                    setCurrentIndex(0);
                    loadRandomSyllabusQuestions();
                }
            }, 5000);
        }
    };

    // Function to shuffle an array
    function shuffleArray(array: any[]) {
        for (let i = array.length - 1; i > 0; i--) {
            const randomIndex = Math.floor(Math.random() * (i + 1));
            [array[i], array[randomIndex]] = [array[randomIndex], array[i]];
        }
        return array;
    }

    useEffect(() => {
        loadRandomSyllabusQuestions();

        document.title =
            "Plants in the Surroundings – Class 3 Science | Mama’s Guidance";
    }, []);

    return (
        <>
            <Helmet>
                <meta name="description" content="Learn about different types of plants around us in Class 3 Science. Discover trees, shrubs, herbs, climbers, and their importance in our surroundings at Mama’s Guidance." />
            </Helmet>
            {!showImage ? (
                <div className="contentPart showCenter additionWord">
                    <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                    <IonModal
                        ref={modal}
                        trigger="open-modal"
                        initialBreakpoint={1}
                        breakpoints={[0, 0.5, 0.75, 1]}
                        handleBehavior="cycle"
                        backdropDismiss={false}  // ✅ Prevents click-to-close
                        className="full-page-modal"
                    >
                        <IonContent className="ion-padding">
                            <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                            <h1>🌿 Chapter 6: Plants in the Surroundings</h1>

                            <section>
                                <h2><span className="emoji">🌱</span>Parts of a Plant and Their Functions</h2>
                                <p>Plants have many parts. Each part has a special job to help the plant live and grow.</p>

                                <h3>1. Roots</h3>
                                <ul>
                                    <li>Hold the plant in the soil</li>
                                    <li>Absorb water and minerals</li>
                                    <li>Store food (e.g., carrot, beetroot)</li>
                                </ul>

                                <h3>2. Stem</h3>
                                <ul>
                                    <li>Supports the plant upright</li>
                                    <li>Carries water and food to all parts</li>
                                    <li>Can be soft (banana) or hard (tree)</li>
                                </ul>

                                <h3>3. Leaves</h3>
                                <ul>
                                    <li>Make food using sunlight (photosynthesis)</li>
                                    <li>Take in carbon dioxide, give out oxygen</li>
                                    <li>Green due to chlorophyll</li>
                                </ul>

                                <h3>4. Flowers</h3>
                                <ul>
                                    <li>Help plants to reproduce</li>
                                    <li>Turn into fruits and seeds</li>
                                    <li>Attract insects for pollination</li>
                                </ul>

                                <h3>5. Fruits</h3>
                                <ul>
                                    <li>Protect seeds inside</li>
                                    <li>Often sweet and edible</li>
                                </ul>

                                <h3>6. Seeds</h3>
                                <ul>
                                    <li>Grow into new plants</li>
                                    <li>Have a baby plant and stored food</li>
                                </ul>

                                <table className="table table-bordered">
                                    <thead>
                                        <tr>
                                            <th>Part</th>
                                            <th>Function</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr><td>Root</td><td>Holds plant; absorbs water</td></tr>
                                        <tr><td>Stem</td><td>Carries food and water</td></tr>
                                        <tr><td>Leaves</td><td>Make food for plant</td></tr>
                                        <tr><td>Flower</td><td>Helps in reproduction</td></tr>
                                        <tr><td>Fruit</td><td>Protects seeds</td></tr>
                                        <tr><td>Seed</td><td>Grows into new plant</td></tr>
                                    </tbody>
                                </table>
                            </section>

                            <section>
                                <h2><span className="emoji">🌰</span>Structure of a Seed and Germination</h2>
                                <p>A seed is the beginning of a new plant.</p>

                                <h3>Parts of a Seed:</h3>
                                <ul>
                                    <li><strong>Seed coat</strong> – Outer covering</li>
                                    <li><strong>Baby plant</strong> – Tiny plant inside</li>
                                    <li><strong>Cotyledon</strong> – Stores food</li>
                                </ul>

                                <h3>What is Germination?</h3>
                                <p>Germination is when a seed starts growing into a plant.</p>
                                <p><strong>Needs:</strong> Water 💧, Air 🌬️, Warmth ☀️</p>
                                <p>The root grows down, and the shoot grows up.</p>
                            </section>

                            <section>
                                <h2><span className="emoji">🌾</span>Kinds of Seeds</h2>
                                <h3>1. Monocot Seeds</h3>
                                <ul>
                                    <li>Have one cotyledon</li>
                                    <li><strong>Examples:</strong> Rice, Wheat, Corn</li>
                                </ul>

                                <h3>2. Dicot Seeds</h3>
                                <ul>
                                    <li>Have two cotyledons</li>
                                    <li><strong>Examples:</strong> Beans, Peas, Mango</li>
                                </ul>
                            </section>

                            <section>
                                <h2><span className="emoji">🌼</span>Care of Plants</h2>
                                <p>Plants need care to grow healthy and strong.</p>
                                <ul>
                                    <li>Water them regularly 💧</li>
                                    <li>Give them sunlight ☀️</li>
                                    <li>Use good soil and manure 🌱</li>
                                    <li>Remove weeds</li>
                                    <li>Protect from pests and animals</li>
                                </ul>
                                <p><strong>Tip:</strong> Don&apos;t pluck leaves or flowers from small plants!</p>
                            </section>

                            <section className="highlight-box">
                                <h2><span className="emoji">🧠</span>Quick Summary</h2>
                                <ul>
                                    <li>Plants have 6 main parts: root, stem, leaves, flowers, fruits, seeds</li>
                                    <li>Each part has a different job</li>
                                    <li>Seeds grow into new plants through germination</li>
                                    <li>There are monocot and dicot seeds</li>
                                    <li>Plants need sunlight, water, and care</li>
                                </ul>
                            </section>

                            <section className="fun-activities">
                                <h2><span className="emoji">📚</span>Fun Activities</h2>
                                <ul>
                                    <li>Soak a bean and watch it grow in wet cotton</li>
                                    <li>Draw and label parts of a plant</li>
                                    <li>Collect different seeds and paste them in a chart</li>
                                    <li>Visit a garden and note how plants are cared for</li>
                                </ul>
                            </section>

                            <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                        </IonContent>
                    </IonModal>
                    <article className="visually-hidden">
                        <h1>🌿 Class 3 Science – Chapter 6: Plants in the Surroundings</h1>

                        <section>
                            <h2><span className="emoji">🌱</span>Parts of a Plant and Their Functions</h2>
                            <p>Plants have many parts. Each part has a special job to help the plant live and grow.</p>

                            <h3>1. Roots</h3>
                            <ul>
                                <li>Hold the plant in the soil</li>
                                <li>Absorb water and minerals</li>
                                <li>Store food (e.g., carrot, beetroot)</li>
                            </ul>

                            <h3>2. Stem</h3>
                            <ul>
                                <li>Supports the plant upright</li>
                                <li>Carries water and food to all parts</li>
                                <li>Can be soft (banana) or hard (tree)</li>
                            </ul>

                            <h3>3. Leaves</h3>
                            <ul>
                                <li>Make food using sunlight (photosynthesis)</li>
                                <li>Take in carbon dioxide, give out oxygen</li>
                                <li>Green due to chlorophyll</li>
                            </ul>

                            <h3>4. Flowers</h3>
                            <ul>
                                <li>Help plants to reproduce</li>
                                <li>Turn into fruits and seeds</li>
                                <li>Attract insects for pollination</li>
                            </ul>

                            <h3>5. Fruits</h3>
                            <ul>
                                <li>Protect seeds inside</li>
                                <li>Often sweet and edible</li>
                            </ul>

                            <h3>6. Seeds</h3>
                            <ul>
                                <li>Grow into new plants</li>
                                <li>Have a baby plant and stored food</li>
                            </ul>

                            <table>
                                <thead>
                                    <tr>
                                        <th>Part</th>
                                        <th>Function</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr><td>Root</td><td>Holds plant; absorbs water</td></tr>
                                    <tr><td>Stem</td><td>Carries food and water</td></tr>
                                    <tr><td>Leaves</td><td>Make food for plant</td></tr>
                                    <tr><td>Flower</td><td>Helps in reproduction</td></tr>
                                    <tr><td>Fruit</td><td>Protects seeds</td></tr>
                                    <tr><td>Seed</td><td>Grows into new plant</td></tr>
                                </tbody>
                            </table>
                        </section>

                        <section>
                            <h2><span className="emoji">🌰</span>Structure of a Seed and Germination</h2>
                            <p>A seed is the beginning of a new plant.</p>

                            <h3>Parts of a Seed:</h3>
                            <ul>
                                <li><strong>Seed coat</strong> – Outer covering</li>
                                <li><strong>Baby plant</strong> – Tiny plant inside</li>
                                <li><strong>Cotyledon</strong> – Stores food</li>
                            </ul>

                            <h3>What is Germination?</h3>
                            <p>Germination is when a seed starts growing into a plant.</p>
                            <p><strong>Needs:</strong> Water 💧, Air 🌬️, Warmth ☀️</p>
                            <p>The root grows down, and the shoot grows up.</p>
                        </section>

                        <section>
                            <h2><span className="emoji">🌾</span>Kinds of Seeds</h2>
                            <h3>1. Monocot Seeds</h3>
                            <ul>
                                <li>Have one cotyledon</li>
                                <li><strong>Examples:</strong> Rice, Wheat, Corn</li>
                            </ul>

                            <h3>2. Dicot Seeds</h3>
                            <ul>
                                <li>Have two cotyledons</li>
                                <li><strong>Examples:</strong> Beans, Peas, Mango</li>
                            </ul>
                        </section>

                        <section>
                            <h2><span className="emoji">🌼</span>Care of Plants</h2>
                            <p>Plants need care to grow healthy and strong.</p>
                            <ul>
                                <li>Water them regularly 💧</li>
                                <li>Give them sunlight ☀️</li>
                                <li>Use good soil and manure 🌱</li>
                                <li>Remove weeds</li>
                                <li>Protect from pests and animals</li>
                            </ul>
                            <p><strong>Tip:</strong> Don&apos;t pluck leaves or flowers from small plants!</p>
                        </section>

                        <section className="highlight-box">
                            <h2><span className="emoji">🧠</span>Quick Summary</h2>
                            <ul>
                                <li>Plants have 6 main parts: root, stem, leaves, flowers, fruits, seeds</li>
                                <li>Each part has a different job</li>
                                <li>Seeds grow into new plants through germination</li>
                                <li>There are monocot and dicot seeds</li>
                                <li>Plants need sunlight, water, and care</li>
                            </ul>
                        </section>

                        <section className="fun-activities">
                            <h2><span className="emoji">📚</span>Fun Activities</h2>
                            <ul>
                                <li>Soak a bean and watch it grow in wet cotton</li>
                                <li>Draw and label parts of a plant</li>
                                <li>Collect different seeds and paste them in a chart</li>
                                <li>Visit a garden and note how plants are cared for</li>
                            </ul>
                        </section>

                    </article>
                    {currentQuestion ? (
                        <>
                            <h3 className="mb-3">{currentQuestion.question}</h3>
                            {(currentQuestion.type === "multiple-choice" ||
                                currentQuestion.type === "fill-in-the-blank") && (
                                    <IonRadioGroup
                                        onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                    >
                                        <IonList>
                                            {(currentQuestion.options || []).map((option, index) => (
                                                <IonItem
                                                    className={`grid-item ${selectedOption === option
                                                        ? option.toLowerCase() === currentQuestion.answer.toLowerCase()
                                                            ? "correct"
                                                            : "errorItems"
                                                        : ""
                                                        }`}
                                                    key={index}
                                                >
                                                    <IonRadio slot="start" value={option} />
                                                    <IonLabel>{option}</IonLabel>
                                                </IonItem>
                                            ))}
                                        </IonList>
                                    </IonRadioGroup>
                                )}
                            {currentQuestion.type === "true-false" && (
                                <IonRadioGroup
                                    onIonChange={(e) => handleAnswerSubmit(e.detail.value)}
                                >
                                    <IonList>
                                        {["true", "false"].map((option, index) => (
                                            <IonItem key={index} className={`${selectedOption === option
                                                ? option.toLowerCase() === String(currentQuestion.answer).toLowerCase()
                                                    ? "correct"
                                                    : "errorItems"
                                                : ""
                                                }`}>
                                                <IonRadio slot="start" value={option} />
                                                <IonLabel>{option.toUpperCase()}</IonLabel>
                                            </IonItem>
                                        ))}
                                    </IonList>
                                </IonRadioGroup>
                            )}
                        </>
                    ) : (
                        <p>Loading question...</p>
                    )}
                </div>
            ) : (
                <div className="contentPart showMiddle">
                    <img
                        style={{ height: "150px" }}
                        className="img-fluid"
                        src={process.env.REACT_APP_WELDONE_LINK}
                        alt="Well done"
                    />
                </div>
            )}
        </>
    );
};

export default SurroundingPlant;
